<template>
  <DashboardLayout>
    <div class="space-y-6">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-secondary-900 dark:text-white">Import & Export Data</h1>
          <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Bulk import or export data for backup, reporting, or migration
          </p>
        </div>
        <button
          @click="showInfo = true"
          class="p-2 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white rounded-full hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
        >
          <InformationCircleIcon class="h-6 w-6" />
        </button>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Import Section -->
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
          <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
            <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
              Import Data
            </h3>
            <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
              Upload CSV files to import tickets, assets, or users in bulk
            </p>
          </div>
          <div class="p-6">
            <div class="space-y-6">
              <!-- Type Selection -->
              <div>
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  What would you like to import?
                </label>
                <select
                  v-model="importType"
                  class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                >
                  <option value="">Select type...</option>
                  <option value="tickets">Tickets</option>
                  <option value="assets">Assets</option>
                  <option value="users">Users</option>
                </select>
              </div>

              <!-- Template Download -->
              <div v-if="importType" class="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
                <div class="flex items-start">
                  <InformationCircleIcon class="h-5 w-5 text-primary-600 dark:text-primary-400 mr-3 mt-0.5 flex-shrink-0" />
                  <div class="flex-1">
                    <p class="text-sm text-primary-900 dark:text-primary-200">
                      Download the template CSV file to ensure your data is formatted correctly
                    </p>
                    <button
                      @click="downloadTemplate"
                      class="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 font-medium underline"
                    >
                      Download {{ importType }} template
                    </button>
                  </div>
                </div>
              </div>

              <!-- File Upload -->
              <div v-if="importType">
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Upload CSV File
                </label>
                <div
                  @drop.prevent="handleDrop"
                  @dragover.prevent="isDragging = true"
                  @dragleave="isDragging = false"
                  :class="[
                    'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
                    isDragging
                      ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-secondary-300 dark:border-secondary-600 hover:border-primary-400 dark:hover:border-primary-600'
                  ]"
                  @click="$refs.fileInput.click()"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    accept=".csv"
                    @change="handleFileSelect"
                    class="hidden"
                  />
                  <CloudArrowUpIcon class="mx-auto h-12 w-12 text-secondary-400 dark:text-secondary-500" />
                  <p class="mt-2 text-sm text-secondary-900 dark:text-white">
                    <span class="text-primary-600 dark:text-primary-400 font-medium">Click to upload</span>
                    <span class="text-secondary-600 dark:text-secondary-400"> or drag and drop</span>
                  </p>
                  <p class="mt-1 text-xs text-secondary-500 dark:text-secondary-400">CSV file up to 10MB</p>
                </div>

                <!-- Selected File -->
                <div v-if="selectedFile" class="mt-4 flex items-center justify-between bg-secondary-50 dark:bg-secondary-700 rounded-lg p-4">
                  <div class="flex items-center">
                    <DocumentTextIcon class="h-8 w-8 text-secondary-400 dark:text-secondary-500 mr-3" />
                    <div>
                      <p class="text-sm font-medium text-secondary-900 dark:text-white">{{ selectedFile.name }}</p>
                      <p class="text-xs text-secondary-500 dark:text-secondary-400">{{ formatFileSize(selectedFile.size) }}</p>
                    </div>
                  </div>
                  <button
                    @click="clearFile"
                    class="text-secondary-400 dark:text-secondary-500 hover:text-secondary-600 dark:hover:text-secondary-300"
                  >
                    <XMarkIcon class="h-5 w-5" />
                  </button>
                </div>

                <!-- Import Button -->
                <button
                  v-if="selectedFile"
                  @click="processImport"
                  :disabled="importing"
                  class="w-full mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  <span v-if="!importing">Import Data</span>
                  <span v-else class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Importing...
                  </span>
                </button>
              </div>

              <!-- Import Results -->
              <div v-if="importResults" class="mt-6 rounded-lg p-4" :class="[
                importResults.failed > 0
                  ? 'bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800'
                  : 'bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800'
              ]">
                <h4 class="text-sm font-medium" :class="[
                  importResults.failed > 0
                    ? 'text-warning-900 dark:text-warning-200'
                    : 'text-success-900 dark:text-success-200'
                ]">
                  Import Complete
                </h4>
                <div class="mt-2 text-sm space-y-1">
                  <p class="text-success-700 dark:text-success-300">
                    ✓ {{ importResults.success }} records imported successfully
                  </p>
                  <p v-if="importResults.failed > 0" class="text-danger-700 dark:text-danger-300">
                    ✗ {{ importResults.failed }} records failed
                  </p>
                  <details v-if="importResults.errors && importResults.errors.length > 0" class="mt-2">
                    <summary class="cursor-pointer text-xs font-medium text-secondary-700 dark:text-secondary-300">
                      View errors ({{ importResults.errors.length }})
                    </summary>
                    <ul class="mt-2 text-xs space-y-1 text-danger-600 dark:text-danger-400">
                      <li v-for="(error, index) in importResults.errors.slice(0, 10)" :key="index">
                        {{ error }}
                      </li>
                      <li v-if="importResults.errors.length > 10" class="text-secondary-600 dark:text-secondary-400">
                        ... and {{ importResults.errors.length - 10 }} more errors
                      </li>
                    </ul>
                  </details>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Export Section -->
        <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
          <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
            <h3 class="text-lg leading-6 font-semibold text-secondary-900 dark:text-white">
              Export Data
            </h3>
            <p class="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
              Download your data in CSV or Excel format
            </p>
          </div>
          <div class="p-6">
            <div class="space-y-6">
              <!-- Export Type -->
              <div>
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  What would you like to export?
                </label>
                <select
                  v-model="exportType"
                  class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                >
                  <option value="">Select type...</option>
                  <option value="tickets">Tickets</option>
                  <option value="work_orders">Work Orders</option>
                  <option value="incidents">Incidents</option>
                  <option value="assets">Assets</option>
                </select>
              </div>

              <!-- Export Filters -->
              <div v-if="exportType" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Date From
                    </label>
                    <input
                      v-model="exportFilters.date_from"
                      type="date"
                      class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Date To
                    </label>
                    <input
                      v-model="exportFilters.date_to"
                      type="date"
                      class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                    />
                  </div>
                </div>

                <!-- Status Filter -->
                <div v-if="['tickets', 'work_orders', 'incidents', 'assets'].includes(exportType)">
                  <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Status (Optional)
                  </label>
                  <select
                    v-model="exportFilters.status"
                    class="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                  >
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              <!-- Export Format -->
              <div v-if="exportType">
                <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Format
                </label>
                <div class="space-y-2">
                  <label class="flex items-center p-3 border border-secondary-300 dark:border-secondary-600 rounded-lg cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors">
                    <input
                      v-model="exportFormat"
                      type="radio"
                      value="csv"
                      class="form-radio text-primary-600 focus:ring-primary-500"
                    />
                    <span class="ml-3 text-sm text-secondary-900 dark:text-white">CSV (Comma Separated Values)</span>
                  </label>
                  <label class="flex items-center p-3 border border-secondary-300 dark:border-secondary-600 rounded-lg cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors">
                    <input
                      v-model="exportFormat"
                      type="radio"
                      value="excel"
                      class="form-radio text-primary-600 focus:ring-primary-500"
                    />
                    <span class="ml-3 text-sm text-secondary-900 dark:text-white">Excel (XLSX)</span>
                  </label>
                </div>
              </div>

              <!-- Export Button -->
              <button
                v-if="exportType"
                @click="processExport"
                :disabled="exporting"
                class="w-full px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                <span v-if="!exporting" class="flex items-center justify-center">
                  <ArrowDownTrayIcon class="h-5 w-5 mr-2" />
                  Export Data
                </span>
                <span v-else class="flex items-center justify-center">
                  <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting...
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <Modal :show="showInfo" @close="showInfo = false">
      <div class="p-6">
        <div class="flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/30 mx-auto">
          <InformationCircleIcon class="h-6 w-6 text-primary-600 dark:text-primary-400" />
        </div>
        <h3 class="mt-4 text-lg font-medium text-secondary-900 dark:text-white text-center">
          Import & Export Data
        </h3>
        <div class="mt-4 space-y-4 text-sm text-secondary-600 dark:text-secondary-400">
          <p>
            <strong class="text-secondary-900 dark:text-white">Import:</strong><br>
            Upload CSV files to bulk-import data. Always download the template first to ensure correct formatting.
          </p>
          <p>
            <strong class="text-secondary-900 dark:text-white">Export:</strong><br>
            Download your data for backup, reporting, or migration purposes. Apply filters to export specific records.
          </p>
          <p>
            <strong class="text-secondary-900 dark:text-white">Tips:</strong><br>
            • Large imports may take several minutes<br>
            • Check the import results for any errors<br>
            • Exports respect your permission levels<br>
            • Downloaded files are automatically deleted after 24 hours
          </p>
        </div>
        <div class="mt-6">
          <button
            @click="showInfo = false"
            class="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 font-medium"
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  InformationCircleIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

const showInfo = ref(false);
const importType = ref('');
const exportType = ref('');
const exportFormat = ref('csv');
const selectedFile = ref(null);
const importing = ref(false);
const exporting = ref(false);
const importResults = ref(null);
const isDragging = ref(false);

const exportFilters = ref({
  date_from: '',
  date_to: '',
  status: '',
});

const handleFileSelect = (event) => {
  selectedFile.value = event.target.files[0];
  isDragging.value = false;
};

const handleDrop = (event) => {
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
  isDragging.value = false;
};

const clearFile = () => {
  selectedFile.value = null;
  importResults.value = null;
};

const downloadTemplate = () => {
  window.location.href = `/import/template/${importType.value}`;
};

const processImport = async () => {
  if (!selectedFile.value) return;

  importing.value = true;
  importResults.value = null;

  const formData = new FormData();
  formData.append('file', selectedFile.value);
  formData.append('type', importType.value);

  router.post('/import/process', formData, {
    onSuccess: (page) => {
      if (page.props.flash.success) {
        importResults.value = page.props.flash.success.results;
      }
    },
    onError: (errors) => {
      console.error('Import errors:', errors);
    },
    onFinish: () => {
      importing.value = false;
    },
  });
};

const processExport = () => {
  exporting.value = true;

  const params = new URLSearchParams({
    type: exportType.value,
    format: exportFormat.value,
    ...Object.fromEntries(
      Object.entries(exportFilters.value).filter(([_, v]) => v !== '')
    ),
  });

  window.location.href = `/export?${params.toString()}`;

  // Reset exporting state after a delay
  setTimeout(() => {
    exporting.value = false;
  }, 2000);
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' bytes';
  if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / 1048576).toFixed(2) + ' MB';
};
</script>
